// Executive Performance Hub for Mining - Data Store
// Modular structure - imports from /mock/ directory
// IMPORTANT: This file aggregates all modular data files to maintain backward compatibility
// The exported structure is EXACTLY the same as the original monolithic file

// Import all modular data
import { companyData, platformData } from './mock/companyData';
import { landingKpisData } from './mock/landingKpisData';
import { kpisData } from './mock/kpisData';
import { areaSummariesData } from './mock/areaSummariesData';
import { alertsData, decisionsData } from './mock/alertsDecisionsData';
import { financeData } from './mock/financeData';
import { revenueBreakdownData, customerConcentrationData } from './mock/revenueData';
import { ebitdaWaterfallData, costBreakdownData, ebitdaMarginTrendData } from './mock/ebitdaData';
import { freeCashFlowDetailsData, cashFlowStatementData, incomeStatementData, netIncomeDetailsData } from './mock/cashFlowData';
import { opexBreakdownData, monthlyOpexData, monthlyNetIncomeData } from './mock/opexData';
import { debtByBankData, capexByDepartmentData, topInvestmentsData, deferredInvestmentsData } from './mock/capexDebtData';
import { marketData, suggestedQuestionsData } from './mock/marketData';
import { costPerCaratData } from './mock/costPerCaratData';
import { personnelCostsData } from './mock/personnelCostsData';
import { cmvmcCostsData } from './mock/cmvmcCostsData';
import { subcontractsData } from './mock/subcontractsData';
import { generalAdminData } from './mock/generalAdminData';
import { commercialCostsData } from './mock/commercialCostsData';
import { balanceSheetData, investedCapitalData, workingCapitalData } from './mock/balanceSheetData';
import { balanceSheetSummaryData } from './mock/balanceSheetSummaryData';
import { externalAuditData as externalAuditDataImport } from './mock/externalAuditData';

// Aggregate all data into the same structure as before
export const mockData = {
  company: companyData,
  platform: platformData,
  landingKpis: landingKpisData,
  kpis: kpisData,
  areaSummaries: areaSummariesData,
  alerts: alertsData,
  decisions: decisionsData,
  finance: financeData,
  revenueBreakdown: revenueBreakdownData,
  customerConcentration: customerConcentrationData,
  ebitdaWaterfall: ebitdaWaterfallData,
  costBreakdown: costBreakdownData,
  ebitdaMarginTrend: ebitdaMarginTrendData,
  freeCashFlowDetails: freeCashFlowDetailsData,
  cashFlowStatement: cashFlowStatementData,
  incomeStatement: incomeStatementData,
  opexBreakdown: opexBreakdownData,
  netIncomeDetails: netIncomeDetailsData,
  monthlyOpex: monthlyOpexData,
  monthlyNetIncome: monthlyNetIncomeData,
  debtByBank: debtByBankData,
  capexByDepartment: capexByDepartmentData,
  topInvestments: topInvestmentsData,
  deferredInvestments: deferredInvestmentsData,
  market: marketData,
  suggestedQuestions: suggestedQuestionsData,
  costPerCarat: costPerCaratData,
  personnelCosts: personnelCostsData,
  cmvmcCosts: cmvmcCostsData,
  subcontracts: subcontractsData,
  generalAdmin: generalAdminData,
  commercialCosts: commercialCostsData,
  balanceSheet: balanceSheetData,
  investedCapital: investedCapitalData,
  workingCapital: workingCapitalData,
  balanceSheetSummary: balanceSheetSummaryData,
};

// Helper functions - same as before
export const getOverallStatus = (year, lang = "en") => {
  const kpis = mockData.landingKpis[year];
  const statusCounts = { strong: 0, inline: 0, attention: 0, pressure: 0 };
  
  Object.values(kpis).forEach(kpi => {
    statusCounts[kpi.status]++;
  });
  
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const score = (statusCounts.strong * 3 + statusCounts.inline * 2 + statusCounts.attention * 1 + statusCounts.pressure * 0) / (total * 3);
  
  if (score >= 0.75) return { status: "strong", label: { en: "Strong Performance", pt: "Desempenho Forte" } };
  if (score >= 0.5) return { status: "inline", label: { en: "On Track", pt: "Em Linha" } };
  if (score >= 0.25) return { status: "attention", label: { en: "Attention Required", pt: "Requer Atenção" } };
  return { status: "pressure", label: { en: "Under Pressure", pt: "Sob Pressão" } };
};

export const getSynthesisMessage = (year, lang = "en") => {
  const messages = {
    2024: {
      en: "Company globally on track, with operational execution below plan and stable liquidity despite CAPEX execution pressure.",
      pt: "Empresa globalmente em linha, com execução operacional abaixo do plano e liquidez estável apesar de pressão na execução de CAPEX."
    },
    2025: {
      en: "Strong financial performance with improved margins and healthy cash position, operational execution recovering.",
      pt: "Forte performance financeira com margens melhoradas e posição de caixa saudável, execução operacional a recuperar."
    },
    2026: {
      en: "Financial resilience maintained with growing EBITDA, moderate operational pressure on ore processing requiring attention.",
      pt: "Resiliência financeira mantida com EBITDA a crescer, pressão operacional moderada no processamento de minério a requerer atenção."
    },
  };
  return messages[year][lang];
};

// Re-export external audit data
export const externalAuditData = externalAuditDataImport;

export default mockData;
